import {
  CanvasProps,
  CanvasWithScreenshotting,
  K3CameraControl,
  useSelectedNumberByVariableKey,
  useSelectedValueByVariableKey,
} from '@k3/core'
import { Bounds, Environment, useContextBridge, Sky } from '@react-three/drei'
import { Suspense, useMemo, useState } from 'react'
import { ReactReduxContext } from 'react-redux'
import { Layout } from '../layout/Layout'
import { Loader } from '../utility/Loader'
import { Keys } from '../utility/utils'
import { ShinyGround } from './ShinyGround'
import { SunovationPanel } from './SunovationPanel'
import { useDimensions } from './useDimensions'
import { Rulers } from './Rulers'

const SunovationContent = ({
  onMounted,
}: {
  onMounted: (isMounted: boolean) => void
}) => {
  const setCurrentBackground = `textures/schadowplatz_2k.hdr`
  const { width, angle } = useDimensions()
  return (
    <>
      <Sky sunPosition={[0, 100, -20]} />
      <ShinyGround />

      <Layout>
        <directionalLight
          position={[0, 5, -3]}
          shadow-mapSize={[1024, 1024]}
          intensity={1.0}
        />
        <Environment files={setCurrentBackground} />

        <group
          name="k3d"
          rotation={[angle, 0, 0]}
          position={[-width / 2, 0.002, 0]}
        >
          <SunovationPanel onMounted={onMounted} />
        </group>
        <Rulers />
      </Layout>
    </>
  )
}

const createCanvasProps = (backgroundColor: string | undefined) => {
  const canvasProps: Omit<CanvasProps, 'children'> = {
    className: 'canvas',
    style: {
      backgroundColor,
    },
    shadows: true,
    camera: {
      position: [0, 0.25, -3],
      fov: 35,
      far: 100,
    },
    onCreated: (e) => {
      // eslint-disable-next-line
      ;(window as any).oc3 = e
    },
  }
  return canvasProps
}

export const SunovationRoot = () => {
  const ContextBridge = useContextBridge(ReactReduxContext)

  const [isMounted, setIsMounted] = useState(false)

  const backgroundColor =
    useSelectedValueByVariableKey(
      Keys.BackgroundColor.Variable
    )?.value?.toString() ?? Keys.BackgroundColor.Default

  const density =
    useSelectedNumberByVariableKey(Keys.FogDensity.Variable) ??
    Keys.FogDensity.Default

  const canvasProps = useMemo(
    () => createCanvasProps(backgroundColor),
    [backgroundColor]
  )

  return (
    <CanvasWithScreenshotting canvasProps={canvasProps}>
      <Suspense fallback={<Loader />}>
        <fogExp2 attach="fog" color={backgroundColor} density={density} />
        <Bounds>
          <K3CameraControl isLoadingSceneOverride={!isMounted}>
            <ContextBridge>
              <SunovationContent onMounted={setIsMounted} />
            </ContextBridge>
          </K3CameraControl>
        </Bounds>
      </Suspense>
    </CanvasWithScreenshotting>
  )
}
